// Iteration 47 Phase 3 — JobParties inline editor.
// Renders inside JobDetail. Reads/writes the 4-party model.
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Users, User, Building2, Shield, DollarSign, Save, X, AlertTriangle, Loader2 } from "lucide-react";

const OWNER_STATUS_OPTIONS = [
  { value: "confirmed", label: "Confirmed (real owner)", color: "text-emerald-400" },
  { value: "unknown", label: "Unknown (needs review)", color: "text-amber-400" },
  { value: "declined", label: "Declined to provide", color: "text-slate-400" },
];

export default function JobParties({ jobId }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get(`/workflow/jobs/${jobId}/parties`)
      .then((r) => {
        setData(r.data);
        setDraft(r.data);
      })
      .catch(() => setData({}));

  useEffect(() => { load(); }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return null;

  const update = (field, value) => setDraft((d) => ({ ...d, [field]: value }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/workflow/jobs/${jobId}/parties`, draft);
      toast.success("Parties updated");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const ownerMatchesShop =
    (data.vehicle_owner_name || data.customer_name || "").toLowerCase().trim() ===
    (data.body_shop_name || "").toLowerCase().trim() && (data.body_shop_name || "").trim();

  const ownerName = data.vehicle_owner_name || data.customer_name;
  const showNote = data._legacy_owner_note;

  return (
    <details className="rounded-xl border border-slate-800 bg-[#0B1120] p-4 group" open={editing || ownerMatchesShop || showNote}>
      <summary className="flex items-center justify-between cursor-pointer">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-blue-400" />
          <span className="font-semibold text-slate-100">Parties (Owner · Body Shop · Insurance · Payer)</span>
          {(ownerMatchesShop || showNote || (data.vehicle_owner_status === "unknown")) && (
            <Badge className="bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[10px]" data-testid="parties-warn">
              <AlertTriangle className="w-2.5 h-2.5 mr-1" /> Needs review
            </Badge>
          )}
        </div>
        {!editing && (
          <Button size="sm" variant="outline" className="border-slate-700" onClick={(e) => { e.preventDefault(); setEditing(true); }} data-testid="parties-edit">
            Edit
          </Button>
        )}
      </summary>

      {showNote && (
        <div className="mt-3 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2" data-testid="parties-note">
          {showNote}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        {/* Vehicle Owner */}
        <div className="space-y-2 p-3 rounded-lg bg-slate-900/40 border border-slate-800">
          <div className="flex items-center gap-1 text-xs uppercase tracking-wider text-slate-400">
            <User className="w-3 h-3" /> Vehicle Owner
          </div>
          {editing ? (
            <>
              <input
                type="text" value={draft.vehicle_owner_name || ""} onChange={(e) => update("vehicle_owner_name", e.target.value)}
                placeholder="Full name" className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm"
                data-testid="edit-owner-name"
              />
              <input
                type="tel" value={draft.vehicle_owner_phone || ""} onChange={(e) => update("vehicle_owner_phone", e.target.value)}
                placeholder="Phone" className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs"
                data-testid="edit-owner-phone"
              />
              <input
                type="email" value={draft.vehicle_owner_email || ""} onChange={(e) => update("vehicle_owner_email", e.target.value)}
                placeholder="Email" className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs"
                data-testid="edit-owner-email"
              />
              <select
                value={draft.vehicle_owner_status || "confirmed"} onChange={(e) => update("vehicle_owner_status", e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs"
                data-testid="edit-owner-status"
              >
                {OWNER_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </>
          ) : (
            <>
              <div className="font-medium text-sm">
                {ownerName || <span className="text-amber-400 text-xs italic">Not set — click Edit</span>}
              </div>
              {data.vehicle_owner_phone && <div className="text-xs text-slate-400">{data.vehicle_owner_phone}</div>}
              {data.vehicle_owner_email && <div className="text-xs text-slate-400">{data.vehicle_owner_email}</div>}
              {data.vehicle_owner_status && (
                <div className={`text-[10px] uppercase tracking-wider ${OWNER_STATUS_OPTIONS.find(o => o.value === data.vehicle_owner_status)?.color || ""}`}>
                  {data.vehicle_owner_status}
                </div>
              )}
            </>
          )}
        </div>

        {/* Body Shop */}
        <div className="space-y-2 p-3 rounded-lg bg-slate-900/40 border border-slate-800">
          <div className="flex items-center gap-1 text-xs uppercase tracking-wider text-slate-400">
            <Building2 className="w-3 h-3" /> Body Shop
          </div>
          {editing ? (
            <input
              type="text" value={draft.body_shop_name || ""} onChange={(e) => update("body_shop_name", e.target.value)}
              placeholder="Shop name" className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm"
              data-testid="edit-body-shop"
            />
          ) : (
            <div className="font-medium text-sm">{data.body_shop_name || <span className="text-slate-500 text-xs">—</span>}</div>
          )}
        </div>

        {/* Insurance */}
        <div className="space-y-2 p-3 rounded-lg bg-slate-900/40 border border-slate-800">
          <div className="flex items-center gap-1 text-xs uppercase tracking-wider text-slate-400">
            <Shield className="w-3 h-3" /> Insurance
          </div>
          {editing ? (
            <input
              type="text" value={draft.insurance_company || ""} onChange={(e) => update("insurance_company", e.target.value)}
              placeholder="Carrier" className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm"
              data-testid="edit-insurance"
            />
          ) : (
            <div className="font-medium text-sm">{data.insurance_company || <span className="text-slate-500 text-xs">—</span>}</div>
          )}
        </div>

        {/* Payer */}
        <div className="space-y-2 p-3 rounded-lg bg-slate-900/40 border border-slate-800">
          <div className="flex items-center gap-1 text-xs uppercase tracking-wider text-slate-400">
            <DollarSign className="w-3 h-3" /> Payer
          </div>
          {editing ? (
            <>
              <select
                value={draft.payer_type || ""} onChange={(e) => update("payer_type", e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs"
                data-testid="edit-payer-type"
              >
                <option value="">— select —</option>
                <option value="insurance">Insurance carrier</option>
                <option value="body_shop">Body shop (wholesale)</option>
                <option value="customer">Customer (owner)</option>
                <option value="split">Split</option>
              </select>
              <input
                type="text" value={draft.payer_name || ""} onChange={(e) => update("payer_name", e.target.value)}
                placeholder="Payer name (if different)" className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs"
                data-testid="edit-payer-name"
              />
            </>
          ) : (
            <>
              <div className="font-medium text-sm">
                {data.payer_type || <span className="text-slate-500 text-xs">Not set (defaults to Insurance)</span>}
              </div>
              {data.payer_name && <div className="text-xs text-slate-400">{data.payer_name}</div>}
            </>
          )}
        </div>
      </div>

      {editing && (
        <div className="mt-4 flex justify-end gap-2">
          <Button size="sm" variant="outline" className="border-slate-700" onClick={() => { setEditing(false); setDraft(data); }} disabled={busy} data-testid="parties-cancel">
            <X className="w-3 h-3 mr-1" /> Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500" data-testid="parties-save">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
            Save
          </Button>
        </div>
      )}

      <div className="mt-4 text-[10px] text-slate-500 leading-relaxed">
        The Vehicle Owner MUST NOT equal the Body Shop or Insurance name. This
        was the Maserati bug: the body shop name was displayed as both Bill To
        and Vehicle Owner in the invoice.
      </div>
    </details>
  );
}
